import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { useDebounce } from '../../hooks/useDebounce';
import { useWmsUserSearch } from '../../hooks/useWmsUsers';
import type { WmsUser } from '../../types';

interface UserPickerProps {
  value: WmsUser | null;
  onChange: (user: WmsUser | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

function formatUserName(user: WmsUser): string {
  if (user.firstname || user.lastname) {
    return [user.firstname, user.lastname].filter(Boolean).join(' ');
  }
  return user.username || user.email;
}

export function UserPicker({
  value,
  onChange,
  placeholder = 'Search for user...',
  disabled = false,
}: UserPickerProps) {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const { data: usersData, isLoading } = useWmsUserSearch(debouncedSearch, !value);

  // Update search field when value changes externally
  useEffect(() => {
    if (value) {
      setSearch(formatUserName(value));
    } else {
      setSearch('');
    }
  }, [value]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearch(newValue);
    setShowDropdown(true);
    // If user clears the input, clear the selection
    if (!newValue && value) {
      onChange(null);
    }
  };

  const handleSelectUser = (user: WmsUser) => {
    onChange(user);
    setSearch(formatUserName(user));
    setShowDropdown(false);
  };

  const handleFocus = () => {
    if (!value) {
      setShowDropdown(true);
    }
  };

  const handleBlur = () => {
    // Delay to allow click on dropdown item
    setTimeout(() => setShowDropdown(false), 200);
  };

  const users = usersData?.users || [];

  return (
    <div className="relative">
      <Input
        value={search}
        onChange={handleSearchChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
      />
      {showDropdown && !value && (
        <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Loading...</div>
          ) : users.length > 0 ? (
            users.map((user) => (
              <button
                key={user._id}
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-accent text-sm"
                onClick={() => handleSelectUser(user)}
              >
                <div className="font-medium">{formatUserName(user)}</div>
                <div className="text-muted-foreground text-xs">{user.email}</div>
              </button>
            ))
          ) : debouncedSearch.length >= 1 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">No users found</div>
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">Type to search users...</div>
          )}
        </div>
      )}
    </div>
  );
}
